import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DropDownList, LookupDialogDimensions } from '@core/interfaces';
import { EventService, ListService, fromList } from '@core/services';
import { dialogAttributes } from '@util/dialog';

import { endpoints } from '@environment';

import { SupplierLookupRequest, SupplierLookupResponseBody } from '#types/shipment';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@shipment/constants';
import { ShipmentActions } from '@shipment/state';
import { SupplierLookupDialogComponent } from '@shipment/supplier-lookup-dialog/supplier-lookup-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ShipmentService {
  supplierLookupDialogSelectedCustomer$ = new BehaviorSubject<string>(null);

  supplierLookupDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  countryCodeList$ = this.store.select(fromList.selectCountryCodeList);
  shipmentTermsList$ = this.store.select(fromList.selectShipmentTermsList);
  shipmentTypesList$ = this.store.select(fromList.selectShipmentTypesList);
  shipperStatusList$ = this.store.select(fromList.selectStateOrProvinceList);
  stateOrProvinceList$ = this.store.select(fromList.selectStateOrProvinceList);

  countryCodeDropdownList$ = new BehaviorSubject<DropDownList>(null);
  shipmentTermsDropdownList$ = new BehaviorSubject<DropDownList>(null);
  shipmentTypesDropdownList$ = new BehaviorSubject<DropDownList>(null);
  shipperStatusDropdownList$ = new BehaviorSubject<DropDownList>(null);
  stateOrProvinceDropdownList$ = new BehaviorSubject<DropDownList>(null);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private listService: ListService,
    private store: Store,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private eventService: EventService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.initializeDropdownLists();
        });
      });
  }

  requestLists() {
    this.store.dispatch(ShipmentActions.RequestLists());
  }

  getSupplierLookup(payload: SupplierLookupRequest) {
    return this.http.post(endpoints.shipment.supplierLookup, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_SHIPMENT_SUPPLIER_LOOKUP_DIALOG),
      map((response: SupplierLookupResponseBody) => response)
    );
  }

  initializeDropdownLists() {
    this.shipmentTermsList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.shipmentTermsDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.shipmentTypesList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.shipmentTypesDropdownList$.next(
        this.listService.getDropdownList(list, {
          key3: 'value',
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
          header3: this.translate('core.services.list.dropdown.header.abbreviation'),
        })
      );
    });

    this.countryCodeList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.countryCodeDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.stateOrProvinceList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.stateOrProvinceDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.shipperStatusList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.shipperStatusDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });
  }

  supplierLookupDialog(custid: string, config: DynamicDialogConfig = {}): DynamicDialogRef {
    this.supplierLookupDialogSelectedCustomer$.next(custid);

    return this.dialogService.open(SupplierLookupDialogComponent, {
      ...dialogAttributes,
      header: this.translate('shipment.supplier_lookup_dialog.dialog.title', { custid }),
      styleClass: 'lookup-dialog',
      ...config,
    });
  }
}
